import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { talentService, talentGroupService } from './../../api/apiService';
import Select, { components } from 'react-select';

function TalentGroupManagement() {
  const navigate = useNavigate();
  const [talentGroups, setTalentGroups] = useState([]);
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupTalents, setNewGroupTalents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTalentGroups();
    fetchTalents();
  }, []);

  // Load talent groups from API
  const fetchTalentGroups = async () => {
    try {
      setLoading(true);
      const response = await talentGroupService.getAllGroups(1, 100);
      
      if (response.data?.success) {
        if (response.data?.data?.groups) {
          const groups = response.data.data.groups.map(group => ({
            id: group.id.toString(),
            name: group.name,
            talentIds: group.talent_ids || [],
            createdAt: group.created_at
          }));
          setTalentGroups(groups);
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          const groups = response.data.data.map(group => ({
            id: group.id.toString(),
            name: group.name,
            talentIds: group.talent_ids || [],
            createdAt: group.created_at
          }));
          setTalentGroups(groups);
        }
      }
    } catch (error) {
      console.error('Error loading talent groups from API:', error);
      setError('Failed to load talent groups');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all talents
  const fetchTalents = async () => {
    try {
      const response = await talentService.getAllTalents(1, 1000);
      const talentsData = response.data?.talents || [];
      const formattedTalents = talentsData.map(talent => ({
        ...talent,
        name: talent.name || talent.Name || talent.function || talent.Funktion || `Talent ${talent.id}`
      }));
      setTalents(formattedTalents);
    } catch (error) {
      console.error('Error fetching talents:', error);
    }
  };

  const handleSave = async () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name');
      return;
    }
    if (newGroupTalents.length === 0) {
      alert('Please select at least one talent for the group');
      return;
    }
    
    const talentIds = newGroupTalents.map(t => t.value);
    try {
      setLoading(true);
      if (editingGroup) {
        const response = await talentGroupService.updateGroup(editingGroup.id, {
          name: newGroupName.trim(),
          talent_ids: talentIds
        });
        if (response.data?.success) {
          alert('Group updated successfully!');
          fetchTalentGroups();
          handleCancel();
        } else {
          throw new Error(response.data?.message || 'Failed to update group');
        }
      } else {
        const response = await talentGroupService.createGroup({
          name: newGroupName.trim(),
          talent_ids: talentIds
        });
        if (response.data?.success && response.data?.data) {
          alert('Group created successfully!');
          fetchTalentGroups();
          handleCancel();
        } else {
          throw new Error(response.data?.message || 'Failed to create group');
        }
      }
    } catch (error) {
      console.error('Error saving talent group:', error);
      alert('Error saving group: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingGroup(null);
    setNewGroupName('');
    setNewGroupTalents([]);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setNewGroupTalents(group.talentIds.map(id => {
      const talent = talents.find(t => 
        t.id === id || 
        t.mysql_id === id || 
        t.talent_id === id ||
        String(t.id) === String(id) ||
        String(t.mysql_id) === String(id)
      );
      return talent ? { 
        value: talent.id, 
        label: `${talent.name || talent.Name || 'Unknown'}${talent.function || talent.Funktion ? ` - ${talent.function || talent.Funktion}` : ''}${talent.bereich || talent.Bereich ? ` (${talent.bereich || talent.Bereich})` : ''}` 
      } : null;
    }).filter(Boolean));
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await talentGroupService.deleteGroup(groupId);
      if (response.data?.success) {
        alert('Group deleted successfully!');
        fetchTalentGroups();
        if (editingGroup?.id === groupId) {
          handleCancel();
        }
      } else {
        throw new Error(response.data?.message || 'Failed to delete group');
      }
    } catch (error) {
      console.error('Error deleting talent group:', error);
      alert('Error deleting group: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const DropdownIndicator = props => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <div style={{ width: '16px', margin: '0' }}>
            <svg version="1.1" id="Isolationsmodus" x="0px" y="0px" viewBox="0 0 322.9 257.78" shapeRendering="geometricPrecision">
              <path xmlns="http://www.w3.org/2000/svg" d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z" />
            </svg>
          </div>
        </components.DropdownIndicator>
      )
    );
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      height: '50px',
      width: '100%',
      margin: '0px',
      borderRadius: '0px',
      border: '1px solid #121212'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#121212' : 'inherit',
      color: state.isSelected ? '#ffffff' : '#121212',
      '&:hover': { 
        backgroundColor: state.isSelected ? '#121212' : '#f7f7f7',
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#121212',
      fontWeight: "700"
    }),
  };

  const filteredGroups = talentGroups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && talentGroups.length === 0) {
    return <div className="content-wrapper">Loading talent groups...</div>;
  }

  return (
    <div className="content-wrapper page">
      <div className='content-wrapper'>
        <div style={{ justifyContent: 'space-between', display: 'flex', marginBottom: '30px', alignItems: 'center' }}>
          <h1>Talent Groups</h1>
          <button
            onClick={() => navigate('/admin/users')}
            className='big-button'
            style={{ backgroundColor: '#000', color: '#fff' }}
          >
            Back to Users
          </button>
        </div>
      </div>

      {/* Create/Edit Form Section */}
      <div className='content-wrapper' style={{ marginBottom: '40px', padding: '30px', backgroundColor: editingGroup ? '#f7f7f7' : '#fff', border: editingGroup ? '2px solid #121212' : '1px solid #121212' }}>
        <h2 style={{ marginBottom: '30px' }}>{editingGroup ? 'Edit Talent Group' : 'Create New Talent Group'}</h2>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase' }}>
            Group Name
          </label>
          <input
            type="text"
            className="inputtext"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="Enter group name"
          />
        </div>

        <div style={{ marginBottom: '30px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase' }}>
            Select Talents ({newGroupTalents.length} selected)
          </label>
          <Select
            isSearchable
            components={{ DropdownIndicator }}
            options={talents
              .filter(t => !newGroupTalents.some(selected => selected.value === t.id))
              .map(t => ({
                value: t.id,
                label: `${t.name || t.Name || 'Unknown'}${t.function || t.Funktion ? ` - ${t.function || t.Funktion}` : ''}${t.bereich || t.Bereich ? ` (${t.bereich || t.Bereich})` : ''}`
              }))}
            value={null}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setNewGroupTalents([...newGroupTalents, selectedOption]);
              }
            }}
            placeholder="Search and select talents to add..."
            styles={customStyles}
          />
          
          {/* Selected Talents List */}
          {newGroupTalents.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '10px', textTransform: 'uppercase' }}>
                Selected Talents ({newGroupTalents.length}):
              </div>
              <div style={{ 
                border: '1px solid #121212', 
                padding: '15px',
                backgroundColor: '#f7f7f7',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {newGroupTalents.map((selectedTalent, index) => {
                  const talent = talents.find(t => 
                    t.id === selectedTalent.value || 
                    t.mysql_id === selectedTalent.value ||
                    String(t.id) === String(selectedTalent.value)
                  );
                  const talentName = talent 
                    ? `${talent.name || talent.Name || 'Unknown'}${talent.Funktion || talent.function ? ` (${talent.Funktion || talent.function})` : ''}${talent.bereich || talent.Bereich ? ` - ${talent.bereich || talent.Bereich}` : ''}`
                    : selectedTalent.label;
                  
                  return (
                    <div 
                      key={selectedTalent.value} 
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 12px',
                        marginBottom: '8px',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd'
                      }}
                    >
                      <span style={{ fontSize: '0.9rem' }}>{talentName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setNewGroupTalents(newGroupTalents.filter((_, i) => i !== index));
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#121212',
                          cursor: 'pointer',
                          fontSize: '18px',
                          fontWeight: '700',
                          padding: '0 8px'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {editingGroup && (
            <button 
              className="button" 
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel Edit
            </button>
          )}
          <button 
            className="big-button" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingGroup ? 'Update Group' : 'Create Group')}
          </button>
        </div>
      </div>

      {/* Groups List Section */}
      <div className='content-wrapper content-wrapper-talent-list' style={{ maxWidth: '100%', width: '100%' }}>
        <div style={{ display: 'flex', marginBottom: '20px', alignItems: 'center', gap: '20px' }}>
          <input
            type="text"
            className='inputtext search'
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flexGrow: 1, maxWidth: '500px' }}
          />
          <div style={{ fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase' }}>
            {filteredGroups.length} Group{filteredGroups.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div style={{ marginTop: '30px', gap: '40px' }} className='list-tasks-header'>
          <div style={{ flexBasis: '300px', paddingLeft: '20px', paddingRight: '20px' }}>Group Name</div>
          <div style={{ flexBasis: '500px', paddingLeft: '20px', paddingRight: '20px' }}>Talents</div>
          <div style={{ flexBasis: '150px', paddingLeft: '20px', paddingRight: '20px' }}>Count</div>
          <div style={{ flexBasis: '300px', paddingLeft: '20px', paddingRight: '20px' }}>Actions</div>
        </div>
        
        <div style={{ marginTop: '0', maxWidth: '100%', width: '100%' }} className='users-container'>
          {filteredGroups.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              {searchTerm ? 'No groups found matching your search.' : 'No groups created yet. Create your first group above!'}
            </div>
          ) : (
            filteredGroups.map(group => (
              <div key={group.id}>
                <div className='list-tasks-item' style={{ gap: '40px' }}>
                  <div style={{ flexBasis: '300px', fontWeight: '700', paddingLeft: '20px', paddingRight: '20px' }}>{group.name}</div>
                  <div style={{ flexBasis: '500px', paddingLeft: '20px', paddingRight: '20px' }}>
                    {group.talentIds.length > 0 ? (
                      <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {group.talentIds.map(talentId => {
                          const talent = talents.find(t => 
                            t.id === talentId || 
                            t.mysql_id === talentId || 
                            t.talent_id === talentId ||
                            String(t.id) === String(talentId) ||
                            String(t.mysql_id) === String(talentId)
                          );
                          const talentName = talent 
                            ? `${talent.name || talent.Name || 'Unknown'}${talent.Funktion || talent.function ? ` (${talent.Funktion || talent.function})` : ''}`
                            : `Talent ID: ${talentId}`;
                          return (
                            <div key={talentId} style={{ marginBottom: '4px', fontSize: '0.9rem' }}>
                              {talentName}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>No talents</span>
                    )}
                  </div>
                  <div style={{ flexBasis: '150px', paddingLeft: '20px', paddingRight: '20px' }}>{group.talentIds.length}</div>
                  <div style={{ flexBasis: '300px', display: 'flex', gap: '10px', alignItems: 'center', paddingLeft: '20px', paddingRight: '20px' }}>
                    <button 
                      className="button" 
                      onClick={() => handleEdit(group)}
                      disabled={loading}
                      style={{ fontSize: '0.85rem', padding: '10px 15px' }}
                    >
                      Edit
                    </button>
                    <button 
                      className="button-red" 
                      onClick={() => handleDelete(group.id)}
                      disabled={loading}
                      style={{ fontSize: '0.85rem', padding: '10px 15px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default TalentGroupManagement;
